import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export type Theme = "light" | "dark";

@Injectable({
    providedIn: "root",
})
export class ThemeService {
    private readonly THEME_STORAGE_KEY = "app-theme";
    private readonly themeSubject: BehaviorSubject<Theme>;
    public theme$: Observable<Theme>;

    constructor() {
        const initialTheme = this.getInitialTheme();
        this.themeSubject = new BehaviorSubject<Theme>(initialTheme);
        this.theme$ = this.themeSubject.asObservable();
        this.applyThemeToDOM(initialTheme);
    }

    /**
     * Gets the initial theme from storage or system preferences
     */
    private getInitialTheme(): Theme {
        // Check localStorage first
        const storedTheme = this.getStoredTheme();
        if (storedTheme) {
            return storedTheme;
        }

        // If no stored theme, check system preference
        const systemTheme = this.getSystemTheme();
        this.saveTheme(systemTheme);
        return systemTheme;
    }

    /**
     * Gets theme from localStorage
     */
    private getStoredTheme(): Theme | null {
        try {
            const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
            if (stored === "light" || stored === "dark") {
                return stored;
            }
        } catch {
            // localStorage may be unavailable in some contexts
        }
        return null;
    }

    /**
     * Gets system color scheme preference
     */
    private getSystemTheme(): Theme {
        if (typeof window !== "undefined" && window.matchMedia) {
            const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
            return prefersDark.matches ? "dark" : "light";
        }
        return "light"; // Default fallback
    }

    /**
     * Saves theme to localStorage
     */
    private saveTheme(theme: Theme): void {
        try {
            localStorage.setItem(this.THEME_STORAGE_KEY, theme);
        } catch {
            // localStorage may be unavailable in some contexts
        }
    }

    /**
     * Gets the current theme value
     */
    public getCurrentTheme(): Theme {
        return this.themeSubject.value;
    }

    /**
     * Applies the theme class to the document body
     */
    private applyThemeToDOM(theme: Theme): void {
        if (typeof document !== "undefined") {
            const body = document.body;
            if (theme === "dark") {
                body.classList.add("dark-theme");
            } else {
                body.classList.remove("dark-theme");
            }
        }
    }

    /**
     * Sets a new theme
     */
    public setTheme(theme: Theme): void {
        if (theme !== this.themeSubject.value) {
            this.saveTheme(theme);
            this.themeSubject.next(theme);
            this.applyThemeToDOM(theme);
        }
    }

    /**
     * Toggles between light and dark theme
     */
    public toggleTheme(): void {
        const newTheme: Theme = this.themeSubject.value === "light" ? "dark" : "light";
        this.setTheme(newTheme);
    }

    /**
     * Listens for system theme changes and updates accordingly
     */
    public watchSystemTheme(): void {
        if (typeof window !== "undefined" && window.matchMedia) {
            const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

            // Modern browsers
            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener("change", (e) => {
                    const newTheme: Theme = e.matches ? "dark" : "light";
                    this.setTheme(newTheme);
                });
            }
            // Legacy browsers
            else if (mediaQuery.addListener) {
                mediaQuery.addListener((e) => {
                    const newTheme: Theme = e.matches ? "dark" : "light";
                    this.setTheme(newTheme);
                });
            }
        }
    }
}
